// Нативная камера в видеокабинете — то, чем кабинет говорит с тем, кто её
// отдаёт. Кабинет (pages/Interviews) о программе для Windows не знает: хозяина
// камеры ему подставляет окно программы (tray/cameraBridge.ts), а в обычном
// браузере, в старой программе и при выключенном тумблере его нет, и кабинет
// открывает камеру сам, как всегда (getUserMedia).
//
// Зачем это вообще. Камеру открывает ОДИН процесс программы и отдаёт видео в два
// места: в звонок (кадры 1280×720 через общую память, nativeCamera.ts) и на диск
// (запись для обучения). Протокол — desktop/hrdeva-calls/README.md, раздел
// «Нативная камера»; зеркало сообщений — tray/bridge.ts и Core/PageProtocol.cs.

/** Почему нативный путь не годится (зеркало `Why` программы). Для страницы это
 *  всегда «открой камеру сама»; слова для человека — cameraWhyText. */
export type CameraWhy =
  | "disabled"
  | "old-host"
  | "no-camera"
  | "virtual-camera"
  | "busy"
  | "privacy"
  | "no-encoder"
  | "timeout"
  | "unsupported"
  | "error";

export const CAMERA_WHYS: readonly CameraWhy[] = [
  "disabled",
  "old-host",
  "no-camera",
  "virtual-camera",
  "busy",
  "privacy",
  "no-encoder",
  "timeout",
  "unsupported",
  "error",
];

/** Почему программа сама закрыла камеру посреди звонка. К этому моменту камера
 *  уже свободна (программа сначала освобождает её, потом пишет странице). */
export type CameraLostWhy = "no-frames" | "unplugged" | "busy" | "error";

export const CAMERA_LOST_WHYS: readonly CameraLostWhy[] = ["no-frames", "unplugged", "busy", "error"];

/** Камера, как её видит программа (имя — для меню «Настройки видео»). */
export interface HostCameraInfo {
  id: string;
  name: string;
}

/** Что программа готова отдавать звонку. Звонок не получает больше этого
 *  (4K в WebRTC-кодек не нужен), запись на диск идёт отдельно и от этого не
 *  зависит. */
export interface CameraCall {
  width: number;
  height: number;
  fps: number;
}

/** Режим звонка по контракту: 1280×720 при 30 к/с. */
export const CALL_MODE: CameraCall = { width: 1280, height: 720, fps: 30 };

/** Ответ программы на «можешь ли ты быть владельцем камеры?». */
export type CameraProbe =
  | { ok: true; v: number; devices: HostCameraInfo[]; call: CameraCall }
  | { ok: false; why: CameraWhy };

/** Раскладка кольца кадров в общей памяти (feedSlots.ts). */
export interface CameraRing {
  /** Сколько слотов в кольце. */
  slots: number;
  /** Шаг между слотами в байтах. */
  stride: number;
  /** Размер заголовка кольца — смещение первого слота. */
  ctrl: number;
  /** Размер пикселей кадра NV12 в слоте. */
  frameBytes: number;
}

/** Цветовая матрица кадра по данным камеры; без метки Chromium считает NV12
 *  матрицей BT.709. */
export type CameraMatrix = "bt709" | "smpte170m";

export interface CameraOpenRequest {
  /** Номер запроса: по нему приходят `camera-lost` и `camera-rec`, им же
   *  закрывают камеру. Придумывает страница. */
  id: string;
  /** Камера из `CameraProbe.devices`; null — по умолчанию. */
  deviceId: string | null;
  width: number;
  height: number;
  fps: number;
}

/** Открытая камера: сведения о кадре и кольцо с кадрами. */
export interface CameraFeed {
  id: string;
  deviceId: string;
  label: string;
  width: number;
  height: number;
  fps: number;
  format: "NV12";
  matrix: CameraMatrix;
  ring: CameraRing;
  /** Кольцо: SharedBuffer WebView2. Воркеру оно уходит целиком (transfer), после
   *  чего здесь пусто — второй раз буфер никому не отдать. */
  buffer: ArrayBufferLike;
}

/** Состояние записи для обучения на стороне программы (плашка кабинета). */
export interface CameraRec {
  id: string;
  on: boolean;
  segment: number;
  why?: string;
}

/** Контекст встречи для описания записи. */
export interface CameraContext {
  meetingId: string;
  candidateId?: string;
  vacancyId?: string;
  recruiterId?: string;
  /** Когда разрешена запись встречи: по данным сервера при входе или, если кандидат
   *  согласился уже в комнате, по часам страницы. */
  recordingGrantedAt?: string;
  /** Откуда известно согласие; не названо — от сервера. */
  consentSource?: "ats" | "room_signal" | "in_person";
}

/** Причина, по которой звонку приходится снижать качество исходящего видео (слова
 *  `qualityLimitationReason` WebRTC; зеркало CallLimit программы). */
export type CameraLimit = "none" | "cpu" | "bandwidth" | "other";

/** Здоровье звонка со стороны страницы (раз в 5 с). Имена полей — те, что читает
 *  программа (PageMessages.ParseCameraHealth); сверяет их tests/transcribe.test.ts. */
export interface CameraHealth {
  /** Причина снижения качества: `qualityLimitationReason` отправляемого видео, а если
   *  кодек звонка упёрся в процессор (LocalTrackCpuConstrained) с прошлого раза, — cpu. */
  limit: CameraLimit;
  /** Сколько миллисекунд основной поток страницы провёл в долгих задачах с прошлого раза. */
  longTaskMs: number;
}

/** Отказ нативного пути с причиной. */
export class CameraError extends Error {
  readonly why: CameraWhy;
  constructor(why: CameraWhy, message?: string) {
    super(message ?? `camera: ${why}`);
    this.name = "CameraError";
    this.why = why;
  }
}

export type Unsubscribe = () => void;

export interface CameraHost {
  /** Умеет ли программа отдавать камеру. Не отклоняется: молчание и отказ —
   *  `{ ok: false }`. Срок ответа ставит вызывающий (nativeCamera.PROBE_TIMEOUT_MS). */
  probe(): Promise<CameraProbe>;
  /** Открыть камеру. Выполняется, когда программа прислала `camera-ready` И
   *  кольцо кадров; иначе отклоняется CameraError. */
  open(req: CameraOpenRequest): Promise<CameraFeed>;
  /** Рассказать программе, о какой встрече идёт речь (описание записи). Можно
   *  до `open` и после, повторно — последнее заменяет прежнее. Запись у программы
   *  начинается, как только камера идёт в звонок и встреча названа, поэтому называть
   *  её надо, когда кабинет уже открыт (при прогреве камеры — нет), а согласие,
   *  данное позже, досказывать этим же вызовом. */
  context(id: string, ctx: CameraContext): void;
  /** Закрыть камеру и освободить её совсем (лампочка гаснет). */
  close(id: string): void;
  /** Как звонку со своей камерой — программа по этому снижает свою работу, а не
   *  звонок. Необязательно: программа без этого сообщения просто не учитывает. */
  health?(id: string, h: CameraHealth): void;
  /** Программа сама закрыла камеру. Возвращает «отписаться». */
  onLost(cb: (id: string, why: CameraLostWhy) => void): Unsubscribe;
  /** Список камер программы изменился. */
  onDevices(cb: (devices: HostCameraInfo[]) => void): Unsubscribe;
  /** Запись для обучения началась или закончилась. */
  onRec(cb: (rec: CameraRec) => void): Unsubscribe;
}

let current: CameraHost | null = null;

/** Окно программы ставит хозяина камеры, когда в нём включён тумблер
 *  «Нативная запись камеры (тест)», и снимает при выключении или уходе. */
export function registerCameraHost(host: CameraHost | null): void {
  current = host;
}

/** null — нативной камеры нет, и кабинет работает как всегда. */
export function getCameraHost(): CameraHost | null {
  return current;
}

/** Слова для рекрутёра, когда нативный путь не включился и камера работает
 *  обычным способом. null — причина ожидаемая (тумблер выключен, программа
 *  старая), говорить об этом незачем. */
export function cameraWhyText(why: CameraWhy): string | null {
  switch (why) {
    case "disabled":
    case "old-host":
      return null;
    case "virtual-camera":
      return "Нативная запись камеры не включилась: это виртуальная камера (OBS и подобные). Камера работает обычным способом.";
    case "no-camera":
      return "Нативная запись камеры не включилась: программа не видит камеру. Камера работает обычным способом.";
    case "busy":
      return "Нативная запись камеры не включилась: камеру держит другая программа. Камера работает обычным способом.";
    case "privacy":
      return "Нативная запись камеры не включилась: Windows не даёт программам доступ к камере (Параметры → Конфиденциальность → Камера). Камера работает обычным способом.";
    case "no-encoder":
      return "Нативная запись камеры не включилась: на этом компьютере нет аппаратного кодера видео. Камера работает обычным способом.";
    case "timeout":
      return "Нативная запись камеры не включилась: камера не успела начать отдавать кадры. Камера работает обычным способом.";
    default:
      return "Нативная запись камеры не включилась. Камера работает обычным способом.";
  }
}

/** Пока программа открывает камеру заново (кнопка камеры, смена камеры): холодный
 *  старт Windows — до 8–9 секунд, и без этой строки кнопка казалась бы мёртвой. */
export const CAMERA_OPENING_TEXT = "Включаем камеру…";

/** Слова для рекрутёра, когда камера отказала посреди встречи и кабинет
 *  вернулся к обычному способу. `pageRecording` — запись для обучения включена и
 *  у страницы: она сама снова пишет камеру рекрутёра, и «прекращена» было бы неправдой. */
export function cameraLostText(why: CameraLostWhy | "stalled", pageRecording = false): string {
  if (why === "unplugged") return "Камеру отключили. Если она подключена, камера работает обычным способом.";
  return pageRecording
    ? "Нативная запись камеры остановилась: камера работает обычным способом, запись для обучения продолжается."
    : "Нативная запись камеры остановилась: камера работает обычным способом, запись камеры для обучения прекращена.";
}
